import { getMessageObjectIfSuccess } from "@api/helpers/realtimeMessageHandlers";
import { MeetingsSchedule } from "@api/models/meetings-schedule/MeetingsSchedule";
import { MScheduleSlot } from "@api/models/meetings-schedule/MeetingsScheduleSlot";
import { UserProfile } from "@api/models/UserProfile";
import { RealtimeMessageModel, SignalRMethod } from "@api/repositories/reatime-repository/types";
import { DateTime } from "luxon";
import { resolve } from "path";
import { Vue } from 'vue-property-decorator';
import { Store } from "vuex";
import { MScheduleDataLoaderInterface } from "./DataLoader";
import { MScheduleDataStorageInterface } from "./DataStorage";
import { MeetingsSlotFabricInterface } from "./helpers/MeetingsSlotFabric";
import { SignalRHelperinterface } from "./helpers/SignalRHelper";
import { ObserverMethodsType } from "./types/ObserverMethodsType"
import { UserRole } from "./types/UserRole";

interface MeetingsScheduleManagerInterface {
    fetchData(eventId: string): void;
    setOwnProfile(profile: UserProfile): void;
}

interface MScheduleServiceParams {
    isAdmin: boolean;
    routeScheduleId?: string;
    userId: number;
}

export class MScheduleService implements MeetingsScheduleManagerInterface {

    selectedSchedule: string;
    timeFrames: DateTime[];
    scheduleOnUpdate = false;

    accessTable: Record<string, string> = {};

    private isAdmin: boolean;
    private routeScheduleId: string | undefined;
    private userId: number;

    constructor(
        private dataLoader: MScheduleDataLoaderInterface,
        private dataStorage: MScheduleDataStorageInterface,
        private slotFabric: MeetingsSlotFabricInterface,
        private signalRHelper: SignalRHelperinterface,
        private store: Store<any>,
        params: MScheduleServiceParams

    ) {

        this.isAdmin = params.isAdmin;
        this.routeScheduleId = params.routeScheduleId;
        this.userId = params.userId;

        const observerMethods = {
            [ObserverMethodsType.schedule]: {
                [SignalRMethod.objectUpdated]: this.onScheduleRealtimeUpdate.bind(this)
            },
            [ObserverMethodsType.slot]: {
                [SignalRMethod.objectUpdated]: this.onSlotRealtimeUpdate.bind(this),
            }
        }

        this.signalRHelper.connectToHub(observerMethods);
    }

    async onScheduleRealtimeUpdate(source: string) {
        try {

            const message: RealtimeMessageModel | null = getMessageObjectIfSuccess(source);

            if (!message) {
                return;
            }

            //удаление 🔶
            if (message.data?.isDeleted) {
                const id = message.objectId;

                if(id) {
                    this.dataStorage.deleteSchedule(id);
                    this.updateSelectedScheduleIfNeeded(id);
                }

                return;
            }

            const schedule = new MeetingsSchedule(message.data);

            if (this.scheduleOnUpdate) {
                //избежание повторной обработки сообщения 🔶
                return
            }

            this.scheduleOnUpdate = true;

            //для родительских ❇️
            if(!schedule.parentId) {

                const existing = this.rootSchedules.find((existingSchedule) => existingSchedule.id === schedule.id);

                if (existing) {

                    //снятие с публикации 🔶
                    if (!message.data.isPublished) {
                        this.dataStorage.deleteSchedule(schedule.id);

                        this.updateSelectedScheduleIfNeeded(schedule.id);
                    }
                }
                else {
                    //добавление нового или публикация 🔶

                    if(schedule.eventId === this.currentEventId) {
                        const paylaod = [...this.rootSchedules, schedule].sort(this.scheduleSortHandler);

                        if (paylaod.length === 1) {
                            this.setSelectedSchedule(paylaod[0].id);
                        }

                        this.dataStorage.setRootSchedules(paylaod);
                    }
                }
            }
            else {
                //для дочерних ❇️

                const targetSchedulesList = this.dataStorage.schedules[schedule.parentId];
                const existing = targetSchedulesList?.find((existingSchedule) => existingSchedule.id === schedule.id);

                if (existing) {
                    //снятие с публикации 🔶
                    if (!message.data.isPublished) {
                        this.dataStorage.deleteConcreteSchedule(schedule.parentId, schedule.id)
                    }
                }
                else {
                    //добавление нового или публикация 🔶
                    const paylaod = [...targetSchedulesList || [], schedule].sort(this.scheduleSortHandler);
                    this.dataStorage.setSchedules(paylaod, schedule.parentId);

                    if (!this.slots[schedule.id]) {
                        this.loadAndUpdateSlots(schedule.id, schedule);
                    }
                }
            }

            setTimeout(() => {
                this.scheduleOnUpdate = false
            }, 200)
        }
        catch (e) {
            this.store.dispatch('reportError', e);
        }
    }

    private onSlotRealtimeUpdate(source: any) {
        try {

            const message: RealtimeMessageModel | null = getMessageObjectIfSuccess(source);

            if (!message || !message.data) {
                return;
            }

            //удаление 🔶
            if (message.data?.isDeleted) {
                const id = message.objectId;

                if (id) {
                    const scheduleId = this.dataStorage.deleteSlot(id);

                    if (scheduleId) {
                        // нужно снимать бронь только если удалился собственный слот
                        if (this.accessTable[scheduleId] === id) {
                            this.setAccesToSchedule(scheduleId, "")
                        }
                    }
                }
                return;
            }

            const slot = new MScheduleSlot(message.data);
            const schedule = this.dataStorage.getScheduleById(slot.scheduleId);

            if ((this.isAdmin || schedule?.userIds?.includes(this.userId)) && slot.ownerId) {
                this.fetchAndSetProfiles([slot.ownerId]);
            }
            else if (slot.ownerId && slot.ownerId === this.userId) {
                this.setOwnProfile()
            }

            this.dataStorage.updateSlot(slot);
        }
        catch (e) {
            this.store.dispatch('reportError', e);
        }
    }

    private updateSelectedScheduleIfNeeded(targetScheduleId: string) {
        if (this.selectedSchedule === targetScheduleId) {
            this.setSelectedSchedule(this.getCurrentScheduleId(this.rootSchedules));
        }
    }

    async fetchData(eventId: string): Promise<void> {
        //fetch root schedules
        const rootSchedules = (await this.dataLoader.fetchRootSchedules(eventId))
            ?.sort(this.scheduleSortHandler);

        if (rootSchedules && rootSchedules?.length > 0) {

            this.dataStorage.setRootSchedules(rootSchedules);
            const rootIds = rootSchedules.map((schedule) => schedule.id);

            //изначальный день - первый не прошедший день или последний в списке
            let currentScheduleId = "";
            if (this.routeScheduleId && rootIds.includes(this.routeScheduleId)) {
                currentScheduleId = this.routeScheduleId
            }
            else {
                currentScheduleId = this.getCurrentScheduleId(rootSchedules);
            }

            this.setSelectedSchedule(currentScheduleId);
        }
    }

    async loadAndUpdateSlots(scheduleId: string, schedule: MeetingsSchedule): Promise<void> {
        const delay = Math.round(Math.random() * 9000);

        setTimeout(async () => {

            const loadedSlots = await this.dataLoader.fetchSlots([scheduleId]);

            const mixedSlots: Record<string, MScheduleSlot[]> = {
                [scheduleId]: this.slotFabric.createSlots(schedule, loadedSlots?.filter((slot) => slot.scheduleId === schedule.id) ?? [])
            };

            this.dataStorage.setSlots({ ...this.slots, ...mixedSlots });

            resolve();

        }, delay);
    }

    /**
     * Возвращает id элемента с началом в первый незаконченный день или последний день
     * элементы должны быть отсортированны
     *
     * @param rootSchedules - родительские элементы
     * @returns
     */
    private getCurrentScheduleId(rootSchedules: MeetingsSchedule[]) {
        return rootSchedules.find(({ from }) =>
            (from?.startOf("day") as DateTime) >= DateTime.local().startOf("day"))?.id || rootSchedules[rootSchedules.length - 1]?.id;
    }

    private async fetchSchedules(parentScheduleId: string) {

        const schedules = await this.dataLoader.fetchSchedules(parentScheduleId);

        if (schedules && schedules.length) {

            const loadedSlots = await this.dataLoader.fetchSlots(schedules.map((schedule) => schedule.id));

            if (loadedSlots) {
                const preparedSlots: Record<string, MScheduleSlot[]> = {};

                schedules?.forEach((schedule) => {
                    //создание слотов
                    preparedSlots[schedule.id] = this.slotFabric.createSlots(schedule, loadedSlots.filter((slot) => slot.scheduleId === schedule.id)) ;
                });

                this.dataStorage.setSchedules(schedules, parentScheduleId);
                this.dataStorage.setSlots(preparedSlots);

                const userIds: number[] = this.selectUserIds(schedules, loadedSlots);

                //сравнить профили с существующими - недстающие догрузить
                this.fetchAndSetProfiles(userIds);
            }
        }
    }

    private scheduleSortHandler(a: MeetingsSchedule, b: MeetingsSchedule) {
        if (a.from && b.from) {
            if (a.from > b.from) { return 1; }
            if (a.from < b.from) { return -1 }
        }
        return 0;
    }

    getRole(speakerIds: number[] | null = []) {
        if (this.isAdmin) {
            return UserRole.admin
        }

        if (speakerIds?.includes(this.userId)) {
            return UserRole.speaker
        }
        else {
            return UserRole.member
        }
    }

    setOwnProfile() {
        this.dataStorage.addUserProfile(this.store.getters['profile']);
    }

    setAccesToSchedule(scheduleId: string, slotId: string) {
        Vue.set(this.accessTable, scheduleId, slotId);
    }

    /**
     * Загружает профили если они еще не загружены
     * @param userIds
     */
    private async fetchAndSetProfiles(userIds: number[]) {
        //если загруженные профили уже есть то фильтруем userIds по загруженным профилям
        if(this.profiles.size > 0) {
            userIds = userIds.filter((userId) => !this.profiles.has(userId));
        }

        //если после фильтрации остались профили для загрузки то загружаем их
        if (userIds.length) {
            const fetchedProfiles = await this.dataLoader.fetchProfiles(userIds);

            //дополняем список загруженных профилей
            const updatedProfilesMap = new Map([...this.profiles.values(), ...fetchedProfiles].map((userProfile) => [userProfile.userId || 0, userProfile]))

            this.dataStorage.setUserProfiles(updatedProfilesMap);
        }
    }

    private selectUserIds(schedules: MeetingsSchedule[], slots: MScheduleSlot[]) {
        const userIds: Set<number> = new Set();

        schedules.forEach((schedule) => {
            if (Array.isArray(schedule.userIds)) {
                schedule.userIds.forEach((userId) => userIds.add(userId))
            }
        })

        slots.forEach((slot) => {
            if (slot.ownerId) {
                userIds.add(slot.ownerId)
            }
        });

        return [...userIds.values()];
    }

    public setSelectedSchedule(scheduleId: string) {

        Vue.set(this, "selectedSchedule", scheduleId);

        if (!this.dataStorage.schedules[scheduleId]) {
            this.fetchSchedules(scheduleId);
        }
    }

    sort(direction = "asc") {
        const schedules = this.currentSchedules.sort((a, b) => {
            const aUserId = a.userIds?.[0];
            const bUserId = b.userIds?.[0];

            if (aUserId && bUserId) {
                const aProfile = this.profiles.get(aUserId)
                const bProfile = this.profiles.get(bUserId)

                if (aProfile && bProfile) {

                    if (aProfile.fullName > bProfile.fullName) {
                        return direction === "desc" ? 1 : -1;
                    }
                    if (aProfile.fullName < bProfile.fullName) {
                        return direction === "asc" ? 1 : -1;
                    }
                }
            }

            return 0;
        })

        this.dataStorage.setSchedules(schedules, this.selectedSchedule);
    }

    get rootSchedules() {
        return this.dataStorage.rootSchedules;
    }

    get currentSchedules() {
        return this.dataStorage.schedules[this.selectedSchedule];
    }

    private get currentEventId() {
        return this.store.state.events.selected
    }

    get profiles() {
        return this.dataStorage.profiles
    }

    get slots() {
        return this.dataStorage.slots;
    }

    destroy() {
        this.signalRHelper.disconnectFromHub();
    }
}